<template>
  <div class="form-builder" ref="formBuilderRef">
    <div class="all-elements question-groups">
      <div class="selected-language">
        <multiselect
          :options="formLanguageList"
          :show-labels="false"
           :searchable="false"
          :allow-empty="false"
          v-model="currentLanguage"
          @input="languageChanged"
          @open="openValue"
          @close="closeValue"
        >
          <template #singleLabel="{ option }">
            <span class="singlelabel-language">{{ getLang('currentLanguage') }}:</span> {{ option }}
          </template>
          <template #option="{ option }">
            {{ option }}
          </template>
          <template #caret><span v-bind:class="{'active':isOpenLanguageSelector}" class="arrow"><i class="sherpa-icon icon-translate"></i></span></template>
        </multiselect>
      </div>
      <div class="fix-elements">
        <simplebar data-simplebar-auto-hide="false">
          <draggable
            class="list-group fix"
            v-bind="dragFixOptions"
            v-model="fixList"
            item-key="id"
            @start="dragAllElementStart"
            @end="dragAllElementEnd"
            :clone="cloneElement"
            :disabled="disabledDraggable"
          >
            <template #item="{ element }">
              <div
                :key="element.id"
                :class="['list-group-item', element.type]"
              >
                <i v-if="element.type === 'group'" class="sherpa-icon icon-copy item-icon"></i>
                <div class="question-description">
                  <span class="name-uppercased">{{ getLang(element.type) }}</span>
                </div>
              </div>
            </template>
          </draggable>
        </simplebar>
      </div>
      <div class="question-elements">
        <div class="searcher-input">
          <input v-model="questionSearcher" @input="doQuestionsSearch()" />
          <transition name="fade" mode="out-in">
            <i v-if="questionSearcher.trim() === ''" class="sherpa-icon icon-buscar"></i>
            <i v-else class="sherpa-icon icon-cerrar" @click="questionSearcherReset()"></i>
          </transition>
          <transition name="fade" mode="out-in">
            <div class="searcher-count-results">{{ questionSearcher.trim() ? `${questionSearcherCount}/${questionSearcherTotal}` : questionSearcherTotal }}</div>
          </transition>
        </div>
        <simplebar data-simplebar-auto-hide="false">
          <draggable
            class="list-group"
            v-bind="dragQuestionOptions"
            v-model="showList"
            item-key="id"
            :disabled="disabledDraggable"
            @start="dragAllElementStart"
            @end="dragAllElementEnd"
            @change="doQuestionsSearch"
          >
            <template #item="{ element }">
              <div
                :key="element.id"
                :class="['list-group-item', element.type]"
              >
                <question-item v-if="containerRef && element.type === 'question'"
                  :element="element"
                  :getName="getName"
                  :getTypeName="getTypeName"
                  :getLang="getLang"
                  :currentLanguage="currentLanguage"
                  :formBuilderContainer="containerRef"
                  :highlightTerm="questionSearcher.trim()"
                  @languageTextPopupOpen="languageTextPopupOpen"
                />
              </div>
            </template>
          </draggable>
        </simplebar>
      </div>
    </div>

    <div class="selected-elements groups">
      <simplebar data-simplebar-auto-hide="false">
        <div class="list-container">
          <!-- Indicador de lista vacía -->
          <transition name="fade" mode="out-in">
            <div
              v-show="selectedElementsList.length === 0 && !dragAllElement"
              class="empty-list-indicator"
            >
              <span class="description">{{ getLang('dropItemsHere') }}</span>
              <i class="sherpa-icon icon-derecha"></i>
            </div>
          </transition>
          <!-- Lista de elementos seleccionados -->
          <draggable
            :class="['list-group', 'selected-group', {'dragging-element' : draggingElement}]"
            v-model="selectedElementsList"
            item-key="id"
            v-bind="dragSelectedOptions"
            :disabled="disabledDraggable"
            @start="dragSelectedElementStart"
            @end="dragSelectedElementEnd"
            @change="elementChanged"
          >
            <template #item="{ element }">
              <div
                :key="element.id"
                :class="['list-group-item', element.type, {'conditional': isConditional(element)}]"
              >
                <group-item v-if="containerRef && element && element.type === 'group'"
                  :element="element"
                  :parent="null"
                  :getName="getName"
                  :getTypeName="getTypeName"
                  :getLang="getLang"
                  :dragAllElement="dragAllElement"
                  :dragSelectedElementQuestion="dragSelectedElementQuestion"
                  :dragSelectedElementGroupId="dragSelectedElementGroupId"
                  :parentGroupIds="[]"
                  :dragSelectedExternalElementGroup="isExternalGroupDragged(element, dragSelectedElementGroupId, null)"
                  :dragSelectedOptions="dragSelectedOptions"
                  :selected="true"
                  :deleteElementFromList="deleteElementFromList"
                  :currentLanguage="currentLanguage"
                  :formBuilderContainer="containerRef"
                  :domainEndpoint="domainEndpoint"
                  :formId="apiDefinitionId"
                  :isConditional="isConditional"
                  :getConditionalList="getConditionalList"
                  :resetConditional="resetConditional"
                  :deactivateSelectedListConditionals="deactivateSelectedListConditionals"
                  :elementChangedConditionalDeactivate="elementChangedConditionalDeactivate"
                  :conditionalSelected="conditionalSelected"
                  :getElementFromSelectedList="getElementFromSelectedList"
                  :resetConditionalValues="resetConditionalValues"
                  @dragSelectedElementStart="dragSelectedChildElementStart"
                  @dragSelectedElementEnd="dragSelectedChildElementEnd"
                  @deleteElementCall="deleteElementCall"
                  @languageTextPopupOpen="languageTextPopupOpen"
                  @newConditionalSelected="newConditionalSelected"
                />
                <question-item v-else-if="containerRef && element && element.type === 'question'"
                  :element="element"
                  :parent="null"
                  :getName="getName"
                  :getTypeName="getTypeName"
                  :getLang="getLang"
                  :selected="true"
                  :currentLanguage="currentLanguage"
                  :formBuilderContainer="containerRef"
                  :domainEndpoint="domainEndpoint"
                  :formId="apiDefinitionId"
                  :deactivateSelectedListConditionals="deactivateSelectedListConditionals"
                  :conditionalSelected="conditionalSelected"
                  @deleteElementCall="deleteElementCall"
                  @languageTextPopupOpen="languageTextPopupOpen"
                />
              </div>
            </template>
          </draggable>
        </div>
      </simplebar>
    </div>

    <div id="type_actions">
      <div class="normal-buttons">
        <div class="buttons w3-twothird" key="buttons">
          <!-- Update buttons -->
          <transition name="fade" mode="out-in">
            <button v-if="apiDefinitionId && type.actions.update" v-on:click="callUpdate()" class="w3-btn w3-round-large update-and-stay" :disabled="validating">
              <transition name="fade" mode="out-in">
                <span v-if="!validating"><i class="far fa-save"></i> {{ getLang('saveAndStay') }}</span>
                <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
              </transition>
            </button>
          </transition>
        </div>
      </div>
    </div>

    <!-- Popup -->
    <transition name="fade" mode="out-in">
      <builder-popup
        v-show="containerRef && popupVisibility"
        :popupContainer="containerRef"
        :isVisible="popupVisibility"
        ref="saveMessage"
        @popupVisibility="changePopupVisibility"
      >
        <div class="popup-container translation">
          <div class="title">
            <i class="sherpa-icon icon-formulario"></i>
            <span>{{ getLang('saveResults') }}</span>
          </div>
          <div class="popup-content operation-results">
            <div v-if="operationResult" class="operation-ok">
              <i class="sherpa-icon icon-ico_ok"></i>
              <span class="operation-description">{{ getLang('saveResultsOk') }}</span>
            </div>
            <div v-else class="operation-ko">
              <i class="sherpa-icon icon-ico_error"></i>
              <span class="operation-description">{{ getLang('saveResultsKo') }}</span>
              <div v-for="(error, index) in operationErrors" class="operation-errors" :key="index">
                <div class="error-id">{{ error.id }}</div>
                <div class="error-text" v-html="error.textError" />
              </div>
            </div>
          </div>
        </div>
      </builder-popup>
    </transition>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import simplebar from 'simplebar-vue'
import * as appTypes from '@/store/actions/app'
import languages from '@/language/languages'
import groupItem from './groupItem'
import questionItem from './questionItem'
import Multiselect from '@/components/shared/Multiselect.vue' // 'vue-multiselect ^2.1.6' component with TMD modifications
import * as api from '../definitionLoader/utils/definitionApi'
import Configuration from '@/configuration'
import moment from 'moment'
import builderPopup from '../builderPopup'

const initialFixList = [
  {
    id: 'group',
    type: 'group',
    subtype: null,
    name: {},
    children: [],
    active: true,
    conditional: {
      active: false,
      id: null
    },
    conditionalValues: null
  }
]

export default {
  name: 'form-builder',
  components: {
    draggable,
    simplebar,
    groupItem,
    questionItem,
    Multiselect,
    builderPopup
  },
  props: [
    'content',
    'type',
    'apiDefinitionId'
  ],
  data () {
    return {
      fixList: null,
      questionsList: null,
      selectedElementsList: null,
      formLanguageList: null,
      showList: [],
      dragAllElement: false,
      dragSelectedElementQuestion: false,
      dragSelectedElementGroup: false,
      dragSelectedElementGroupId: null,
      parentGroupIds: [],
      questionSearcher: '',
      questionSearcherCount: 0,
      questionSearcherTotal: 0,
      currentLanguage: null,
      isOpenLanguageSelector: false,
      disabledDraggable: false,
      domainEndpoint: null,
      validating: false,
      popupVisibility: false,
      operationResult: false,
      operationErrors: [],
      refsReady: false,
      dragStartY: null,
      conditionalSelected: []
    }
  },
  beforeMount () {
    this.builderLoaded()
    this.doQuestionsSearch()
    this.doSetLanguage()
  },
  mounted () {
    this.$nextTick(() => {
      this.refsReady = true
      this.conditionalSelected = []
      this.doLoadConditionalDomain(this.selectedElementsList)
    })
  },
  methods: {
    builderLoaded () {
      if (this.content) {
        this.$emit('refreshDefinitionTitle', this.content.formName)
        initialFixList.forEach(element => {
          if (element.name) {
            element.name = {}
            this.content.languagesList.forEach(language => {
              element.name[language] = ''
            })
          }
        })
        this.fixList = JSON.parse(JSON.stringify(initialFixList))
        this.questionsList = JSON.parse(JSON.stringify(this.content.questionsList))
        this.selectedElementsList = JSON.parse(JSON.stringify(this.content.selectedList))
        this.formLanguageList = JSON.parse(JSON.stringify(this.content.languagesList))
        this.domainEndpoint = this.content.domainEndpoint
        this.$emit('definitionLoaded', this.content)
      }
      this.$store.dispatch(`${appTypes.APP_NAMESPACE}/${appTypes.APP_LOADING}`, false)
    },
    doLoadConditionalDomain (list) {
      if (list) {
        list.forEach(element => {
          if (element.conditional && element.conditional.active && element.conditional.id) {
            this.$nextTick(() => {
              this.conditionalSelected.push(element.conditional.id)
            })
          }
          this.doLoadConditionalDomain(element.children)
        })
      }
    },
    doSetLanguage () {
      let appLanguage = this.$ml.current.toLowerCase()
      if (appLanguage && this.formLanguageList.includes(appLanguage)) {
        this.currentLanguage = appLanguage
      } else {
        this.currentLanguage = this.formLanguageList[0]
      }
    },
    closeValue () {
      this.isOpenLanguageSelector = false
    },
    openValue () {
      this.isOpenLanguageSelector = true
    },
    languageChanged (elem) {
    },
    dragAllElementStart () {
      this.dragAllElement = true
    },
    dragAllElementEnd () {
      this.dragAllElement = false
    },
    dragSelectedElementStart (event) {
      this.dragStartY = event.originalEvent.clientY
      if (this.selectedElementsList[event.oldIndex] && this.selectedElementsList[event.oldIndex].type === 'question') {
        this.dragSelectedElementQuestion = true
      } else if (this.selectedElementsList[event.oldIndex] && this.selectedElementsList[event.oldIndex].type === 'group') {
        this.dragSelectedElementGroup = true
        this.dragSelectedElementGroupId = this.selectedElementsList[event.oldIndex].id
      }
    },
    dragSelectedElementEnd () {
      this.dragSelectedElementQuestion = false
      this.dragSelectedElementGroup = false
      this.dragSelectedElementGroupId = null
    },
    elementChanged (event) {
      this.elementChangedConditionalDeactivate(event, this.selectedElementsList)
    },
    elementChangedConditionalDeactivate (event, list) {
      if (event.added) {
        if (event.added.element.subtype === 'domain') {
          this.deactivateSelectedListConditionals(event.added.element, list, false, event.added.newIndex)
        } else if (event.added.element.type === 'group') {
          this.deactivateGroupSelectedListConditionals(event.added.newIndex, list)
          this.deactivateGroupConditionals(event.added.element)
        }
      } else if (event.moved) {
        if (event.moved.newIndex > event.moved.oldIndex && event.moved.element.subtype === 'domain') {
          this.deactivateSelectedListConditionals(event.moved.element, list, false, event.moved.newIndex)
        } else if (event.moved.newIndex < event.moved.oldIndex && event.moved.element.type === 'group') {
          this.deactivateGroupSelectedListConditionals(event.moved.newIndex, list)
          this.deactivateGroupConditionals(event.moved.element)
        }
      }
    },
    deactivateGroupSelectedListConditionals (newIndex, list) {
      for (let i = newIndex; i < list.length; i++) {
        let element = list[i]
        if (element.subtype === 'domain') {
          this.deactivateSelectedListConditionals(element, list, false, i)
        }
      }
    },
    deactivateGroupConditionals (element) {
      if (element.conditional) {
        const conditionalList = this.getConditionalList(element.id)
        let found = false
        for (let i = 0; i < conditionalList.length && !found; i++) {
          found = conditionalList[i].key === element.conditional.id
        }
        if (!found) {
          this.resetConditional(element)
        }
      }
      if (element.children) {
        element.children.forEach(child => {
          this.deactivateGroupConditionals(child)
        })
      }
    },
    dragSelectedChildElementStart (element, event) {
      this.dragStartY = event.originalEvent.clientY
      if (element && element.type === 'question') {
        this.dragSelectedElementQuestion = true
      } else if (element && element.type === 'group') {
        this.dragSelectedElementGroup = true
        this.dragSelectedElementGroupId = element.id
      }
    },
    dragSelectedChildElementEnd () {
      this.dragSelectedElementQuestion = false
      this.dragSelectedElementGroup = false
      this.dragSelectedElementGroupId = null
    },
    isExternalGroupDragged (element, selectedGroupId) {
      return this.dragSelectedElementGroup && selectedGroupId !== null && !(element && element.id === selectedGroupId)
    },
    cloneElement (element) {
      let newElement = JSON.parse(JSON.stringify(element))
      newElement.id = `group-${Date.now()}`
      newElement.active = true
      return newElement
    },
    getLang (key) {
      return languages[this.$ml.current].formBuilder[key]
    },
    getName (names, language) {
      let name = names[language]
      if (!name || name === '') {
        return `<span class='empty-name'>${this.getLang('emptyName')}</span>`
      } else {
        return name
      }
    },
    getTypeName (element, selected) {
      if (element) {
        if (element.subtype === 'domain') {
          return this.getLang('typeDomain')
        } else if (element.subtype === 'date') {
          return this.getLang('typeDate') + ' <em class="type-extra">(' + element.textRegexp + ')</em>'
        } else {
          return this.getLang('typeText') + (selected ? ' <em class="type-extra">[' + element.minTextLength + '/' + element.maxTextLength + '] (' + element.textRegexp + ')</em>' : '')
        }
      } else {
        return ''
      }
    },
    doQuestionsSearch () {
      this.questionSearcherCount = 0
      this.questionSearcherTotal = 0
      this.showList = []
      for (var i = 0; i < this.questionsList.length; i++) {
        var question = this.questionsList[i]

        // Verificar si el id de la pregunta ya está en la lista seleccionada
        const isInSelectedList = this.isIdInListRecursive(this.selectedElementsList, question.id)

        if (!isInSelectedList) {
          this.questionSearcherTotal += 1
          if (!this.questionSearcher || this.questionSearcher.trim() === '') {
            this.questionSearcherCount += 1
            this.showList.push(question)
          } else {
            if (
              this.normalizeText(question.name[this.currentLanguage]).indexOf(this.normalizeText(this.questionSearcher.trim())) > -1 ||
              this.normalizeText(question.id).indexOf(this.normalizeText(this.questionSearcher.trim())) > -1
            ) {
              this.questionSearcherCount += 1
              this.showList.push(question)
            }
          }
        }
      }
    },
    normalizeText (text) {
      return text
        .toLowerCase() // Convertir a minúsculas
        .normalize('NFD') // Descomponer caracteres acentuados
        .replace(/[\u0300-\u036f]/g, '') // Eliminar diacríticos
    },
    isIdInListRecursive (list, id) {
      for (const item of list) {
        if (item.id === id) {
          return true
        }
        // Si hay un atributo children, llamar recursivamente
        if (item.children && Array.isArray(item.children)) {
          if (this.isIdInListRecursive(item.children, id)) {
            return true
          }
        }
      }
      return false
    },
    questionSearcherReset () {
      this.questionSearcher = ''
      this.doQuestionsSearch()
    },
    deleteElementCall (element) {
      this.deleteElementFromList(element, this.selectedElementsList)
    },
    deleteElementFromList (element, list) {
      element.active = true
      this.resetConditional(element)
      const index = list.findIndex(el => el.id === element.id)
      if (index !== -1) {
        this.deactivateSelectedListConditionals(element, list, true, null)
        list.splice(index, 1)
        this.deactivateParentConditionals(element.id, this.selectedElementsList)
        this.doQuestionsSearch()
      }
    },
    resetConditional (element) {
      if (element.conditional) {
        element.conditional.active = false
        element.conditional.id = null
        this.resetConditionalValues(element.children)
      }
    },
    resetConditionalValues (list) {
      if (list) {
        list.forEach(element => {
          element.conditionalValues = null
        })
      }
    },
    deactivateSelectedListConditionals (element, list, fromDeleteElement, endIndex) {
      const finalList = list !== null ? list : this.selectedElementsList
      this.deactivateConditionals(element, finalList, fromDeleteElement, endIndex)
      if (!fromDeleteElement) {
        this.deactivateParentConditionals(element.id, this.selectedElementsList)
      }
    },
    deactivateParentConditionals (originElementId, list) {
      let found = false
      if (originElementId && list) {
        if (this.elementActiveNotInList(originElementId, list)) {
          for (let i = 0; i < list.length && !found; i++) {
            const element = list[i]
            if (element.conditional && originElementId === element.conditional.id) {
              this.resetConditional(element)
            }
            found = this.deactivateParentConditionals(originElementId, element.children)
          }
        } else {
          found = true
        }
      }
      return found
    },
    elementActiveNotInList (elementId, list) {
      let notFound = true
      if (list) {
        for (let i = 0; i < list.length && notFound; i++) {
          const element = list[i]
          if (element.id === elementId && element.active) {
            notFound = false
          }
        }
      }
      return notFound
    },
    getAllIdsFromParent (list, elementId, add) {
      const result = []
      if (list) {
        list.forEach(element => {
          if (element.id === elementId || add) {
            result.push(element.id)
            result.push(...this.getAllIdsFromParent(element.children, elementId, true))
          }
        })
      }
      return result
    },
    deactivateConditionals (originElement, list, fromDeleteElement, endIndex) {
      if (list) {
        const finalIndex = endIndex === null ? list.length : endIndex
        for (let i = 0; i < finalIndex; i++) {
          let element = list[i]
          const conditionalList = this.getConditionalList(element.id)
          if (element.conditional &&
            (
              (originElement.id === element.conditional.id) ||
              (fromDeleteElement && conditionalList.length === 1) ||
              (!fromDeleteElement && conditionalList.length === 0)
            )
          ) {
            this.resetConditional(element)
          }
          this.deactivateConditionals(originElement, element.children, fromDeleteElement, null)
        }
      }
    },
    languageTextPopupOpen (open) {
      this.disabledDraggable = open
    },
    callUpdate () {
      this.validating = true
      api.call({domainEndpoint: Configuration.value('API_SERVICES_URL') + moment().valueOf(), url: this.type.actions.update.endpoint, token: null, version: null, method: 'POST', lang: this.$ml.current.toLowerCase(), authorization: window.localStorage.getItem('user-token'), data: { id: this.apiDefinitionId, code: null, jsonData: { id: this.apiDefinitionId, appLang: this.$ml.current, selectedList: this.selectedElementsList } }})
        .then(resp => {
          if (resp.requestResult) {
            this.operationResult = true
            this.popupVisibility = true
          } else {
            this.operationErrors = resp.requestResultError.requestCause
            this.operationResult = false
            this.popupVisibility = true
          }
          this.validating = false
        })
        .catch(err => {
          console.error(err)
          this.operationResult = false
          this.operationErrors = [
            {
              id: this.getLang('errorUnknownId'),
              textError: this.getLang('errorUnknownText')
            }
          ]
          this.popupVisibility = true
          this.validating = false
        })
    },
    changePopupVisibility (visibility) {
      this.popupVisibility = visibility
    },
    isConditional (element) {
      return element && element.active && element.conditional && element.conditional.active && this.getConditionalList(element.id).length > 0
    },
    getConditionalList (elementId) {
      const result = []
      this.addConditionalItems(result, this.selectedElementsList, elementId)
      return result
    },
    addConditionalItems (resultsList, selectedElementsList, elementId) {
      let result = false
      if (selectedElementsList) {
        for (let i = 0; i < selectedElementsList.length && !result; i++) {
          let element = selectedElementsList[i]
          if (element.id !== elementId) {
            if (element.type === 'question' && element.subtype === 'domain' && element.active) {
              resultsList.push({
                key: element.id,
                name: element.id
              })
            }
            result = this.addConditionalItems(resultsList, element.children, elementId)
          } else {
            result = true
          }
        }
      }
      return result
    },
    newConditionalSelected (conditional) {
      this.conditionalSelected = []
      if (conditional) {
        this.conditionalSelected.push(conditional.key)
      }
    },
    getElementFromSelectedList (id) {
      return this.getElementById(id, this.selectedElementsList)
    },
    getElementById (id, list) {
      let result = null
      if (list) {
        for (let i = 0; i < list.length && result === null; i++) {
          let element = list[i]
          if (element.id === id) {
            result = element
          } else {
            result = this.getElementById(id, element.children)
          }
        }
      }
      return result
    }
  },
  computed: {
    dragFixOptions () {
      return {
        animation: 200,
        group: {
          name: 'fix',
          put: false,
          pull: 'clone'
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: false
      }
    },
    dragQuestionOptions () {
      return {
        animation: 200,
        group: {
          name: 'question',
          put: false,
          pull: true
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: false
      }
    },
    dragSelectedOptions () {
      return {
        animation: 200,
        group: {
          name: 'selected',
          put: ['fix', 'question', 'selected'],
          pull: true
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: true, // Habilitamos el sort para poder reordenar
        move: (evt, originalEvent) => {
          if (evt.from === evt.to && evt.related) {
            const rect = evt.related.getBoundingClientRect()
            const cursorY = originalEvent.clientY
            const movingDown = cursorY > this.dragStartY // Detecta si el cursor está moviéndose hacia abajo

            if (movingDown) {
              // Si venimos de arriba, permitimos reordenar solo si pasamos el 75% inferior
              if (cursorY < rect.top + rect.height * 0.75) {
                return false
              }
            } else {
              // Si venimos de abajo, permitimos reordenar solo si estamos en el 25% superior
              if (cursorY > rect.top + rect.height * 0.25) {
                return false
              }
            }
          }
          return true
        }
      }
    },
    draggingElement () {
      return this.dragAllElement || this.dragSelectedElementQuestion || this.dragSelectedElementGroup
    },
    containerRef () {
      return this.refsReady ? this.$refs['formBuilderRef'] : null
    }
  }
}
</script>

<style lang="scss">
.form-builder {
  position: fixed;
  left: 111px;
  right: 12px;
  bottom: 70px;
  top: 133px;
  display: flex;
  gap: 10px;
  background-color: #fafafa;

  /* Animaciones para fade */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-enter-active {
    transform: translateY(-15px);
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-20px);
  }
  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  /* Animaciones para fade-slide-left */
  .fade-slide-left-enter-active,
  .fade-slide-left-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-slide-left-enter-active {
    transform: translateX(-20px);
  }
  .fade-slide-left-enter-from,
  .fade-slide-left-leave-to {
    opacity: 0;
    transform: translateX(-25px);
  }
  .fade-slide-left-enter-to,
  .fade-slide-left-leave-from {
    opacity: 1;
    transform: translateX(0);
  }

  /* Animaciones para fade-slide-right */
  .fade-slide-right-enter-active,
  .fade-slide-right-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-slide-right-enter-active {
    transform: translateX(20px);
  }
  .fade-slide-right-enter-from,
  .fade-slide-right-leave-to {
    opacity: 0;
    transform: translateX(25px);
  }
  .fade-slide-right-enter-to,
  .fade-slide-right-leave-from {
    opacity: 1;
    transform: translateX(0);
  }

  .selected-language {
    padding: 15px 10px 0;

    .multiselect__content-wrapper {
      position: absolute !important;
      border-radius: 0;
      border-right: 1px solid #999;
      border-left: 1px solid #999;
      border-bottom: 1px solid #999;

      .multiselect__option {
        color: #35495e;
        font-size: 15px;
        font-family: 'Source Sans Pro';
        font-weight: normal;
      }
      .multiselect__option--selected,
      .multiselect__option--highlight {
        background-color: #8bfaf6;
      }
      .multiselect__option--selected {
        font-weight: 600;
      }
    }

    .arrow {
      top: 3px;

      i {
        font-size: 22px;
      }

      &.active {
        transform: scaleX(-1);
      }
    }

    .singlelabel-language {
      font-weight: 500;
      font-style: italic;
      color: #9f9f9f;
    }

    .multiselect__tags {
      background-color: #e4fffe;
      border: 1px solid #ddd;
      border-radius: 0;
      transition: border 0.2s ease;
      padding: 2px 40px 0 8px !important;

      &:hover {
        border: 1px solid #999;
      }

      .multiselect__single {
        background-color: #e4fffe;
        font-family: 'Source Sans Pro';
        font-size: 15px;
        font-weight: 600;
      }
    }

    .multiselect--active {
      .multiselect__tags {
        border-top: 1px solid #999;
        border-right: 1px solid #999;
        border-left: 1px solid #999;
        border-bottom: 0 !important;
      }
    }
  }

  .all-elements {
    flex-basis: 18%;
    max-width: 18%;
    min-width: 18%;
    display: flex;
    flex-direction: column;
    background-color: #f5f5f5;
    border: 1px dotted #999;
    overflow: hidden;
    position: relative;
    z-index: 0;

    .fix-elements {
      margin-top: 8px;
    }

    .question-elements {
      position: absolute;
      top: 145px;
      left: 0;
      bottom: 0;
      right: 0;

      [data-simplebar] {
        height: 90%;
        margin-top: 8px;
      }

      .question-item {
        display: flex;
        gap: 2px;
      }
    }
  }

  .selected-elements {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background-color: #e4fffe;
    border: 1px dotted #999;

    [data-simplebar] {
      height: 100%;
    }

    .list-group {
      & > span {
        display: block;
        min-height: 110px;
        transition: border 0.2s ease;
        border: 1.5px dashed transparent;
        position: absolute;
        margin-bottom: 5px;
        top: 5px;
        left: 5px;
        right: 5px;
        padding: 5px;
        transition: all 0.2s ease;
      }
      &.dragging-element {
        & > span {
          border: 1.5px dashed #d42f12cb;
        }
      }
      &.selected-group {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        height: 90px;
      }
    }

    .list-group-item {
      .question-description {
        padding-right: 250px;
      }
      &.group {
        padding: 0;
        position: relative;

        &.conditional {
          background-color: #feffc9;
          border: 1px dotted #bf830c;

          &:hover {
            border: 1px solid #bf830c;
          }

          > .group-item > .question-description {
            padding-right: 605px;
          }
        }
      }
    }

    .language-text {
      padding-right: 60px;
    }
  }

  .list-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
  }

  .empty-list-indicator {
    text-align: center;
    color: #888;
    font-style: italic;
    padding: 28px 15px 24px;
    margin: 5px;
    border: 1px dashed #999;
    background-color: #f6f6f6;
    pointer-events: none;
    font-size: 15px;
    font-weight: 400;
    font-family: 'Source Sans Pro';

    i {
      display: block;
      transform: rotate(90deg);
      font-size: 20px;
      margin: 0 auto;
    }
  }

  .list-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 80px;
    margin: 0;
    padding: 10px;

    &.fix {
      min-height: initial;
    }
  }

  .list-group-item {
    cursor: move;
    padding: 0;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 5px;
    font-family: 'Source Sans Pro';
    font-size: 16px;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      border: 1px solid #999;
    }

    &.group {
      background-color: #e8e8e8;
      padding: 26px 13px 24px;
      margin: 0;

      .question-description {
        .name-uppercased {
          text-transform: uppercase;
          font-weight: 600;
          padding-left: 6px;
        }
      }

      .empty-list-indicator {
        i {
          margin: 5px auto;
        }
      }
    }

    i {
      font-size: 18px;
      margin-right: 4px;
      color: #7f7f7f;
      width: 18px;
      vertical-align: top;

      &.item-icon {
        position: absolute;
      }
    }

    .question-description {
      display: inline-block;
      line-height: 18px;
      padding-left: 22px;
      width: 100%;

      .description {
        font-size: 14px;
        color: #707070;
        padding: 3px 2px 3px 5px;
        display: block;
        word-break: break-all;

        &.type {
          font-size: 13px;
          color: #9f9f9f;
          display: inline-block;
          border: 1px dotted transparent;

          em {
            font-size: 11px;
          }
        }
      }
    }

    .edition-element {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: nowrap;
      gap: 4px;

      i {
        font-size: 19px;
        color: #d42f12cb;
        transition: color 0.3s ease;
        cursor: pointer;
        margin-left: 3px;
        flex-shrink: 0;
        line-height: 0;

        &:hover {
          color: #d42f12;
        }
      }
    }
  }

  .ghost {
    opacity: 0.8;
    background-color: #c8ebfb;
  }

  .searcher-input {
    padding: 10px 10px 0;

    input {
      border: 0;
      outline: none;
      font-family: 'Source Sans Pro';
      padding: 5px 65px 5px 10px;
      border: 1px solid #ddd;
      width: 100%;
      transition: border 0.2s ease;

      &:hover,
      &:focus {
        border: 1px solid #999;
      }
    }

    i {
      position: absolute;
      right: 20px;
      top: 18px;
      color: #d42e12;

      &.icon-cerrar {
        cursor: pointer;
      }
    }

    .searcher-count-results {
      position: absolute;
      right: 41px;
      top: 21px;
      font-size: 10px;
      font-style: italic;
      color: #9f9f9f;
    }
  }

  .builder-select {
    display: block;
    text-align: right;
    padding-right: 2px;

    .multiselect {
      height: 26px !important;
      min-height: 26px !important;

      .multiselect__select,
      .multiselect__tags {
        height: 26px !important;
        min-height: 26px !important;
      }

      .multiselect__select {
        padding: 0;
        width: 30px;
        height: 25px;
        min-height: 25px;
        line-height: 15px;

        &:before {
          color: #d42e12;
          border-color: #d42e12 transparent transparent transparent;
        }
      }

      .multiselect__tags {
        background-color: #e4fffe;
        border: 1px solid #ddd;
        padding: 3px 30px 0 8px;
        border-radius: 0;
        min-width: 200px;

        .multiselect__single {
          padding: 0;
          background-color: transparent;
          margin-bottom: 0;
          font-size: 13px;
          color: #333;
          font-weight: 600;
          font-family: 'Source Sans Pro';
        }

        .multiselect__placeholder {
          display: none;
        }
      }

      .multiselect__content-wrapper {
        position: absolute !important;
        z-index: 1000 !important;
        border: 1px solid #ddd;
        width: 200px;
        right: 0;
        left: 0;

        .multiselect__option {
          font-size: 13px;
          padding: 6px 8px;
          min-height: 26px !important;
        }

        .multiselect__option--highlight,
        .multiselect__option--selected {
          background-color: #e4fffe;
          color: #333;
        }
        .multiselect__option--selected {
          font-weight: 600;
        }
      }
    }

    &.action {
      .multiselect__content-wrapper {
        border-radius: 0 !important;
        top: -41px !important;
        left: -1px;
        right: -1px;
      }
    }
  }

  .popup {
    overflow: auto;

    .popup-container {
      min-width: 1000px;
      font-family: 'Source Sans Pro';
      text-transform: none;

      .title {
        color: #666;
        padding: 8px 11px 6px;
        font-size: 15px;
        font-weight: 500;
        border-bottom: 1px solid #999;
        background-color: #e8e8ee;
        line-height: 15px;

        i {
          font-size: 20px;
          color: #666;
          margin-right: 5px;
          vertical-align: middle;
        }
      }

      .popup-content {
        min-width: 1000px;

        &.operation-results {
          padding: 10px 15px;

          .operation-description {
            font-size: 14px;
            font-family: 'Lucida Sans', 'Lucida Sans Regular', 'Lucida Grande', 'Lucida Sans Unicode', Geneva, Verdana, sans-serif;
            font-weight: 300;
          }

          i {
            vertical-align: text-top;
            font-size: 16px;
            margin-right: 10px;

            &.icon-ico_ok {
              color: #4ab800c5;
            }

            &.icon-ico_error {
              color: #e30000;
            }
          }

          .operation-errors {
            width: 100%;
            padding: 5px 0 4px 30px;

            div {
              display: block;
            }

            .error-id {
              color: #e30000;
              font-weight: 600;
            }

            .error-text {
              color: #454545;
            }
          }
        }

        .translation-language {
          display: flex;
          transition: all 0.2s ease;
          border: 1px dotted #aaa;

          &:hover {
            cursor: pointer;

            .language-key {
              color: #000;
              background-color: #8ab6f4be;
            }

            .name {
              background-color: #fff;
            }
          }

          .language-key {
            padding: 7px 11px 7px;
            min-width: 75px;
            font-size: 15px;
            font-weight: 500;
            background-color: transparent;
            border-right: 1px dotted #aaa;
            color:#076cfabe;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all 0.2s ease;
          }

          .name {
            width: 100%;
            display: inline-block;
            transition: all 0.2s ease;
            background-color: #f5f5f5;
            position: relative;

            > span {
              padding: 8px 15px 7px;
              display: inline-block;
              user-select: text;
              cursor: text;

              > p {
                margin: 0;
              }
            }

            .quillWrapper {
              border: 0;

              .ql-toolbar {
                background-color: #e8e9f7;

                &.ql-snow {
                  border: 0;
                }
              }
            }

            .buttons-container {
              position: absolute;
              top: 11px;
              right: 10px;

              .icon-borrar,
              .icon-translate {
                color: #d9614b;
                transition: all 0.2s ease;
                margin-right : 4px;

                &:hover {
                  color: #d42f12;
                }
              }

              .spinner-loading {
                display: inline-block;
              }

              &.selected {
                top: initial;
                bottom: 5px;
              }
            }
          }

          &.selected {
            .language-key {
              color: #000;
              font-weight: 600;
              background-color: #8ab6f4be;
            }

            .name {
              background-color: #fff;
            }
          }
        }
      }
    }
  }

  .empty-name {
    font-size: 16px;
    font-style: italic;
    color: #afafaf;
    font-weight: 300;
    text-transform: none;
  }
}
</style>
